<template>
  <div class="travel-publish">
    <el-form :model="form" :rules="rules" ref="formRef" label-width="110px">
      <!-- 路线模版选择 -->
      <el-form-item
        :label="$t('routeTemplate')"
        prop="templateId"
        class="custom-form-item"
      >
        <div class="form-item-content">
          <el-select
            v-model="form.templateId"
            filterable
            :placeholder="$t('clickSelectRoute')"
            @select="handleSelect"
            @focus="formSubmitted = false"
            :disabled="loading"
          >
            <!-- 自定义空状态提示 -->
            <template slot="empty">
              <p class="empty-text">{{ $t("noTemplateTip") }}</p>
            </template>
            <el-option
              v-for="item in templates"
              :key="item.id"
              :label="
                item.route_name +
                ' / ' +
                $t('trip') +
                '：' +
                item.days +
                $t('day')
              "
              :value="item.id"
            ></el-option>
          </el-select>
          <el-button
            type="primary"
            icon="el-icon-plus"
            @click="navigateToAddTemplate"
            class="add-template-btn"
          >
            {{ $t("createNewRoute") }}
          </el-button>
        </div>
        <!-- 无模版提示 -->
        <el-alert
          v-if="templates.length === 0 && !loading"
          :title="$t('noTemplateAvailable')"
          type="info"
          :closable="false"
          show-icon
        >
        </el-alert>
      </el-form-item>
      <!-- 出游日期选择 -->
      <el-form-item
        :label="$t('travelDate')"
        prop="departureDate"
        class="custom-form-item"
      >
        <div class="form-item-content">
          <el-date-picker
            v-model="form.departureDate"
            type="date"
            :placeholder="$t('selectTravelDate')"
            @focus="formSubmitted = false"
            style="width: 100%"
            :picker-options="pickerOptions"
            :disabled="loading"
          ></el-date-picker>
        </div>
      </el-form-item>
      <!-- 表单按钮 -->
      <el-form-item class="form-actions">
        <el-button
          type="default"
          @click="resetForm"
          class="btn-cancel"
          :disabled="loading"
        >
          {{ $t("cancel") }}
        </el-button>
        <el-button
          type="primary"
          @click="submitForm"
          class="btn-submit"
          :loading="submitting"
        >
          <i class="el-icon-check"></i> {{ $t("confirm") }}
        </el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false, // 页面加载状态
      submitting: false, // 提交状态
      pickerOptions: {
        disabledDate(time) {
          // 只能选择今天之后的日期（包含今天）
          return time.getTime() < Date.now() - 8.64e7;
        },
      },
      templates: [], // 从接口获取的模板列表
      form: {
        templateId: null,
        templateName: "",
        departureDate: null,
      },
      targetTemplateId: "",
      // ✅ 这里删掉原来的 rules！！只保留其他数据
      formSubmitted: false,
    };
  },

  // ✅ 把 rules 放在这里
  computed: {
    rules() {
      return {
        templateId: [
          {
            required: true,
            message: this.$t("pleaseSelectRouteTemplate"),
            trigger: "blur",
          },
        ],
        departureDate: [
          {
            required: true,
            message: this.$t("pleaseSelectTravelDate"),
            trigger: "change",
          },
        ],
      };
    },
  },

  created() {
    this.targetTemplateId = this.$route.query.templateId || "";
    this.checkIsAuth();
    // 页面加载时获取模板列表
    this.getTemplates();
  },

  methods: {
    //查询用户是否已经行商户认证
    checkIsAuth() {
      this.$get("/get_is_auth").then((res) => {
        if (res.data.code != 1) {
          this.$message({
            message: this.$t("pleaseDoMerchantAuthFirst"), //提示的信息
            type: "warning", //类型是成功
            duration: 800, //显示时间, 毫秒。设为 0 则不会自动关闭
            onClose: () => {
              this.$router.push({ path: "/about-me/merchant-auth" }); //测试
            },
          });
        }
      });
    },
    async getTemplates() {
      this.loading = true;
      try {
        const response = await this.$get("/travel/get_templates");
        if (response.data.code === 1) {
          this.templates = response.data.data || [];
          // 关键优化：1. 先判断是否有目标ID；2. 处理类型匹配（字符串转数字）
          if (this.targetTemplateId && this.templates.length > 0) {
            // 将路由参数（字符串）转成和模板ID一致的类型（如数字）
            const targetId = Number(this.targetTemplateId);
            // 匹配模板
            const matchedTemplate = this.templates.find(
              (item) => item.id === targetId, // 类型一致才能匹配成功
            );
            // 匹配成功则设置默认选中
            if (matchedTemplate) {
              this.form.templateId = targetId;
              // 顺便同步 templateName（可选，看你的业务是否需要）
              this.form.templateName = matchedTemplate.route_name;
            }
          }
        } else {
          this.$message.error(
            response.data.msg || this.$t("getTemplateFailed"),
          );
          this.templates = [];
        }
      } catch (error) {
        console.error("获取模板列表失败:", error);
        this.$message.error(this.$t("networkErrorGetTemplateFailed"));
        this.templates = [];
      } finally {
        this.loading = false;
      }
    },
    handleSelect(templateId) {
      const selectedTemplate = this.templates.find(
        (item) => item.id === templateId,
      );
      if (selectedTemplate) {
        this.form.templateName = selectedTemplate.name;
      }
    },
    navigateToAddTemplate() {
      this.$router.push("/travel/route-publish");
    },
    // 提交表单到接口
    async submitForm() {
      this.$refs.formRef.validate(async (valid) => {
        if (valid) {
          this.submitting = true;
          try {
            // 准备提交数据（转换为后端需要的格式）
            const submitData = {
              travel_id: this.form.templateId,
              departure_date: this.form.departureDate
                ? this.formatDate(this.form.departureDate)
                : null,
            };
            // 调用发布接口
            const response = await this.$post(
              "/travel/travel_plan_publish",
              submitData,
            );
            if (response.data.code === 1) {
              this.$message({
                message: this.$t("tripPublishSuccessWaitReview"),
                type: "success",
                duration: 1000,
                showClose: true,
              });
              // 可以跳转到行程列表页
              setTimeout(() => {
                this.$router.push("/about-me/my-listings/tourist-lists");
              }, 1000);
            } else {
              this.$message.error(
                response.data.msg || this.$t("publishFailedRetry"),
              );
            }
          } catch (error) {
            console.error("提交行程失败:", error);
            this.$message.error(this.$t("networkErrorPublishFailed"));
          } finally {
            this.submitting = false;
          }
        } else {
          this.formSubmitted = true;
          return false;
        }
      });
    },
    resetForm() {
      this.$refs.formRef.resetFields();
      this.formSubmitted = false;
    },
    // 格式化日期为YYYY-MM-DD
    formatDate(date) {
      if (!date) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style scoped>
.main-content {
  padding-top: 20px;
}
.travel-publish {
  background: #f9fafb;
  padding: 40px;
  border-radius: 10px;
}
/* 表单容器基础样式 */
::v-deep .el-form {
  background: #ffffff;
  padding: 40px;
  margin: auto;
  width: 80%;
}
::v-deep .el-form-item__label {
  text-align: left !important;
  padding-right: 15px !important;
}
::v-deep .el-form-item__content {
  width: 50%;
}
/* 自定义表单项样式 */
::v-deep .custom-form-item {
  margin-bottom: 25px; /* 增加项间距 */
}
.empty-text {
  padding: 10px 0;
  margin: 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}
/* 表单项内容容器 */
.form-item-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
/* 选择框样式 */
::v-deep .el-select {
  width: 100% !important;
}
::v-deep .el-alert--info.is-light {
  margin-top: 20px;
}
/* 添加模板按钮 */
.add-template-btn {
  width: 100%;
}
/* 按钮区域 */
.form-actions {
  display: flex;
  gap: 15px;
  margin-top: 20px;
  padding-top: 10px;
  border-top: 1px solid #f5f5f5;
}
/* 手机端适配*/
@media (max-width: 768px) {
  ::v-deep .el-form-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  ::v-deep .el-form-item__label {
    width: 100% !important;
    text-align: left !important;
    margin-bottom: 8px;
    padding: 0 !important;
    font-size: 15px;
    font-weight: 500;
  }
  ::v-deep .el-form-item__content {
    width: 100% !important;
    margin-left: 0 !important;
  }
  .travel-publish {
    padding: 20px 15px;
  }
  ::v-deep .el-form {
    padding: 20px 15px;
  }
}
/* 小屏手机额外适配 */
@media (max-width: 375px) {
  .form-actions {
    flex-direction: column;
    gap: 10px;
  }
  .btn-cancel,
  .btn-submit {
    width: 100%;
  }
}
/* 错误提示优化 */
::v-deep .el-form-item__error {
  padding-top: 4px;
  font-size: 12px;
  margin-top: 5px;
}
</style>
